import React, { useEffect } from 'react';
import {
  Button,
  LoadingButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Typography,
} from '@one-thd/sui-atomic-components';
import { setDeleteCardOverlayAnalytics } from '../../../utils/payments-analytics';
import { IModalBasicProps } from '../../../models/common/modal';
import { CardType } from '../../../constants/payments';
import {
  IPaymentCardCommonData,
  IPaymentsPagination,
} from '../../../models/common/payments';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { getCardImagePath } from '../../../utils/credit-cards';
import { IMAGES_ASSETS_URL } from '../../../constants/links';
import { getFormattedKeytag } from '../../../utils/payments';

export const ICDefaultCardMsg =
  "This is also your Instant Checkout card. You'll need to set up how you pay before you can shop with Instant Checkout again.";

export type TDeleteCardAcceptedCardTypes =
  | CardType.GIFT_CARD
  | CardType.CREDIT_CARD
  | CardType.PAYPAL_ACCOUNT
  | CardType.CHECKING_ACCOUNT
  | CardType.KEY_TAG;

// may eventually change type to Record<CardType ...>
const headerMappings: Record<
  TDeleteCardAcceptedCardTypes,
  (card: IPaymentCardCommonData, isAdmin: boolean) => React.ReactNode
> = {
  [CardType.CREDIT_CARD]: (card, isAdmin) => {
    if (card.citiAccountId && isAdmin) {
      return 'Delete Home Depot Credit Cards';
    }
    return card.isDefault
      ? 'Do You Want To Delete Your Default Card?'
      : 'Do You Want To Delete This Card?';
  },
  [CardType.GIFT_CARD]: () => {
    return 'Do You Want To Delete This Gift Card?';
  },
  [CardType.PAYPAL_ACCOUNT]: () => 'Do You Want To Delete This PayPal Account?',
  [CardType.CHECKING_ACCOUNT]: () =>
    'Do You Want To Delete This Checking Account?',
  [CardType.KEY_TAG]: () => 'Do You Want To Delete This Keytag?',
};

// may eventually change type to Record<CardType ...>
const buttonMappings: Record<
  TDeleteCardAcceptedCardTypes,
  (card: IPaymentCardCommonData, isAdmin: boolean) => React.ReactNode
> = {
  [CardType.CREDIT_CARD]: (card, isAdmin) => {
    if (card.citiAccountId && isAdmin) {
      return 'Yes, Delete All Cards';
    }
    return card.isDefault ? 'Yes, Delete Default Card' : 'Yes, Delete Card';
  },
  [CardType.GIFT_CARD]: () => {
    return 'Yes, Delete Gift Card';
  },
  [CardType.PAYPAL_ACCOUNT]: () => 'Yes, Delete PayPal Account',
  [CardType.CHECKING_ACCOUNT]: () => 'Yes, Delete Checking Account',
  [CardType.KEY_TAG]: () => 'Yes, Delete Keytag',
};

const childrenMapping: Record<
  TDeleteCardAcceptedCardTypes,
  (
    card: IPaymentCardCommonData,
    isB2B: boolean,
    isAdmin: boolean,
    paginationInfo?: IPaymentsPagination | null,
    hasProAllowance?: boolean,
    hasHdWallet?: boolean,
  ) => React.ReactNode
> = {
  [CardType.CREDIT_CARD]: (
    card,
    isB2B,
    isAdmin,
    paginationInfo,
    hasProAllowance,
    hasHdWallet,
  ) => {
    const {
      cardBrand,
      cardType,
      cardNumberLast4,
      cardExpiryMonth,
      cardExpiryYear,
      citiAccountId,
      instantCheckoutDefault,
    } = card;
    const showAdminCitiText = isAdmin && citiAccountId;
    const groupedCitiCard =
      paginationInfo &&
      paginationInfo.citiCardsSummary?.find(
        (groupedCard: { citiAccountId: string | undefined }) =>
          groupedCard.citiAccountId === citiAccountId,
      );

    const showProAllowanceMessage = isAdmin && hasProAllowance;
    const showHdWalletMessage = hasHdWallet;

    return (
      <>
        {showAdminCitiText ? (
          <>
            <Typography weight="bold">
              Are you sure you want to proceed? This action will remove all
              credit cards associated with this Citi Account ID from your Pro
              Xtra account.
            </Typography>
            <div className="sui-flex sui-gap-4 sui-mb-4">
              <div className="sui-flex sui-gap-2">
                <img
                  width={33}
                  height="100%"
                  src={getCardImagePath(cardBrand, cardType)}
                  alt="Credit card logo"
                />
                <br />
                <br />
                <Typography weight="bold"> ID{citiAccountId}</Typography>
                <Typography>
                  ({groupedCitiCard?.totalNumberOfCards} associated cards)
                </Typography>
              </div>
            </div>
            <br />
            <Typography>
              If you want to reference your transaction history made with these
              cards, you can do so using the purchaser&apos;s account phone
              number.
            </Typography>
            <br />
            <Typography>
              If you add these cards to another Pro Xtra account in the future,
              you will lose all in-store purchase history associated with these
              cards.
            </Typography>
          </>
        ) : (
          <>
            <div className="sui-flex sui-gap-4 sui-mb-4">
              <div className="sui-flex sui-gap-2">
                <img
                  width={33}
                  height="100%"
                  src={getCardImagePath(cardBrand, cardType)}
                  alt="Credit card logo"
                />
                <Typography> ****{cardNumberLast4}</Typography>
                <Typography>
                  {cardExpiryMonth &&
                    cardExpiryYear &&
                    `Exp ${cardExpiryMonth}/${cardExpiryYear.slice(-2)}`}
                </Typography>
              </div>
            </div>
            <br />
            {instantCheckoutDefault && (
              <div className="sui-mb-4">
                <Typography>{ICDefaultCardMsg}</Typography>
              </div>
            )}
            <Typography>
              You&apos;ll still be able to review all purchase history
              associated with this credit card in your account.
            </Typography>
            {(showProAllowanceMessage || showHdWalletMessage) && (
              <div className="sui-pt-2">
                <Typography>This action will:</Typography>
                <ul className="sui-pt-2 sui-pl-2 sui-list-disc sui-list-inside [&_li_p]:sui-inline">
                  {showProAllowanceMessage && (
                    <li>
                      <Typography>
                        Disable any Pro Allowances currently being charged to
                        this card
                      </Typography>
                    </li>
                  )}
                  {showHdWalletMessage && (
                    <li>
                      <Typography>
                        Remove this card from your HD Pass
                      </Typography>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </>
    );
  },
  [CardType.GIFT_CARD]: (card) => {
    return (
      <>
        <div className="sui-flex sui-flex-wrap sui-mb-12">
          <GiftCardLogoAndNumber card={card} />
        </div>
        <Typography variant="body-base">
          You&apos;ll still be able to review all purchase history associated
          with this gift card in your account.
        </Typography>
      </>
    );
  },
  [CardType.PAYPAL_ACCOUNT]: (card) => {
    const { paypalAccountId } = card;
    return (
      <div className="sui-w-full">
        <div className="sui-flex sui-mb-6 sui-items-center sui-flex-wrap sui-w-full">
          <img
            alt="paypal logo"
            width={57}
            height="100%"
            className="sui-inline-block sui-mr-4"
            src={`${IMAGES_ASSETS_URL}/paypal-logo-v2.svg`}
          />
          <div className="sui-overflow-hidden sui-w-[200px] sm:sui-w-auto">
            <Typography truncate variant="body-lg" color="subtle">
              {paypalAccountId}
            </Typography>
          </div>
        </div>
        <br />
        <Typography>
          You&apos;ll still be able to review all purchase history associated
          with this PayPal Account in your account.
        </Typography>
      </div>
    );
  },
  [CardType.CHECKING_ACCOUNT]: (card) => {
    const { cardNumberLast4 } = card;
    return <Typography>Account ****{cardNumberLast4}</Typography>;
  },
  [CardType.KEY_TAG]: (card) => {
    return (
      <div className="sui-pb-6">
        <Typography variant="body-lg">{getFormattedKeytag(card)}</Typography>
      </div>
    );
  },
};

export interface IDeleteCardProps {
  card: IPaymentCardCommonData;
  cardType: TDeleteCardAcceptedCardTypes;
  onConfirmDelete: () => void;
  isLoading: boolean;
  isB2B: boolean;
  paginationInfo?: IPaymentsPagination | null;
  isAdmin: boolean;
  hasProAllowance?: boolean;
  hasHDWallet?: boolean;
}

export const DeleteCardModal = ({
  card,
  cardType,
  hideModal,
  onConfirmDelete,
  isLoading,
  isB2B,
  paginationInfo,
  isAdmin,
  hasProAllowance,
  hasHDWallet,
}: IDeleteCardProps & IModalBasicProps) => {
  const headerText = headerMappings[cardType](card, isAdmin);
  const buttonText = buttonMappings[cardType](card, isAdmin);
  const children = childrenMapping[cardType](
    card,
    isB2B,
    isAdmin,
    paginationInfo,
    hasProAllowance,
    hasHDWallet,
  );

  useEffect(() => {
    if (card.citiAccountId && isAdmin) {
      setDeleteCardOverlayAnalytics();
    }
  }, [card.citiAccountId, isAdmin]);

  return (
    <>
      <ModalHeader onClose={hideModal}>{headerText}</ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter position="end">
        <div className="sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto">
          <Button variant="secondary" onClick={hideModal}>
            No
          </Button>
          <LoadingButton
            loading={isLoading}
            variant="primary"
            onClick={onConfirmDelete}
            data-testid={`${cardType}-${
              card.isDefault ? 'default' : 'add'
            }-delete-confirm-btn`}
          >
            {buttonText}
          </LoadingButton>
        </div>
      </ModalFooter>
    </>
  );
};

DeleteCardModal.displayName = 'DeleteCardModal';
